import { Button, Drawer, Input, Select, Space, DatePicker } from 'antd';
import { Filter, Search, X } from 'lucide-react';
import { useState } from 'react';
import {
  WIRE_PAYMENT_PRISM_STATUSES,
  WIRE_PAYMENT_PRISM_STATUSES_TEXT,
  WIRE_PAYMENT_PROCESSING_STATUSES,
  WIRE_PAYMENT_PROCESSING_STATUSES_TEXT,
  WIRE_PAYMENT_STATUSES,
} from './utils/useWirePayments';

const { RangePicker } = DatePicker;

interface WirePaymentsFilterProps {
  onFilter: (params: {
    search?: string;
    processingStatus?: WIRE_PAYMENT_PROCESSING_STATUSES[];
    status?: WIRE_PAYMENT_STATUSES[];
    depositedDateRange?: [string, string];
    rejectedDateRange?: [string, string];
    prismStatus?: string[];
    contractCare?: boolean;
  }) => void;
}

const WirePaymentsFilter = ({ onFilter }: WirePaymentsFilterProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedProcessingStatus, setSelectedProcessingStatus] = useState<
    WIRE_PAYMENT_PROCESSING_STATUSES[]
  >([]);
  const [selectedStatuses, setSelectedStatuses] = useState<
    WIRE_PAYMENT_STATUSES[]
  >([]);
  const [depositedDateRange, setDepositedDateRange] = useState<
    [string, string] | null
  >(null);
  const [rejectedDateRange, setRejectedDateRange] = useState<
    [string, string] | null
  >(null);
  const [prismStatus, setPrismStatus] = useState<string[]>([]);
  const [contractCare, setContractCare] = useState<boolean | null>(null);

  const handleSearch = (value: string) => {
    setSearchValue(value);
    onFilter({
      search: value,
      processingStatus:
        selectedProcessingStatus?.length > 0
          ? selectedProcessingStatus
          : undefined,
      status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
    });
  };

  const handleProcessingStatusFilter = (
    values: WIRE_PAYMENT_PROCESSING_STATUSES[]
  ) => {
    setSelectedProcessingStatus(values);
  };

  const handleStatusFilter = (values: WIRE_PAYMENT_STATUSES[]) => {
    setSelectedStatuses(values);
  };

  const handleApplyFilters = () => {
    onFilter({
      search: searchValue,
      processingStatus: selectedProcessingStatus.length
        ? selectedProcessingStatus
        : undefined,
      status: selectedStatuses.length ? selectedStatuses : undefined,
      depositedDateRange: depositedDateRange || undefined,
      rejectedDateRange: rejectedDateRange || undefined,
      prismStatus: prismStatus.length ? prismStatus : undefined,
      contractCare: contractCare !== null ? contractCare : undefined,
    });

    setIsDrawerOpen(false);
  };

  const handleClearFilters = () => {
    setSearchValue('');
    setSelectedProcessingStatus([]);
    setSelectedStatuses([]);
    setDepositedDateRange(null);
    setRejectedDateRange(null);
    onFilter({ search: '' });
    setIsDrawerOpen(false);
    setPrismStatus([]);
    setContractCare(null);
  };

  return (
    <div className="mb-4 flex items-center gap-4 justify-between">
      <div className="flex-1 max-w-md">
        <Input
          placeholder="Search by property address"
          prefix={<Search size={16} className="text-gray-400" />}
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
          allowClear
        />
      </div>
      <Button
        type="primary"
        icon={<Filter size={16} />}
        onClick={() => setIsDrawerOpen(true)}
      >
        Filters
      </Button>

      <Drawer
        title="Filters"
        placement="right"
        onClose={() => setIsDrawerOpen(false)}
        open={isDrawerOpen}
        width={400}
        extra={
          <Space>
            <Button onClick={handleClearFilters} icon={<X size={16} />}>
              Clear
            </Button>
            <Button type="primary" onClick={handleApplyFilters}>
              Apply
            </Button>
          </Space>
        }
      >
        <div className="flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Processing Status
            </label>
            <Select
              mode="multiple"
              placeholder="Select status"
              value={selectedProcessingStatus}
              onChange={handleProcessingStatusFilter}
              allowClear
              style={{ width: '100%' }}
              options={Object.values(WIRE_PAYMENT_PROCESSING_STATUSES).map(
                (status) => ({
                  label: WIRE_PAYMENT_PROCESSING_STATUSES_TEXT[status],
                  value: status,
                })
              )}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Status
            </label>
            <Select
              mode="multiple"
              placeholder="Select statuses"
              value={selectedStatuses}
              onChange={handleStatusFilter}
              allowClear
              style={{ width: '100%' }}
              options={Object.values(WIRE_PAYMENT_STATUSES).map((status) => ({
                label: status.charAt(0).toUpperCase() + status.slice(1),
                value: status,
              }))}
            />
          </div>

          <div className="w-full mr-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Deposited Date
            </label>
            <RangePicker
              onChange={(dates, dateStrings) => {
                setDepositedDateRange(dateStrings as [string, string]);
              }}
              style={{ width: '95%' }}
            />
          </div>

          <div className="w-full mr-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rejected Date
            </label>
            <RangePicker
              onChange={(dates, dateStrings) => {
                setRejectedDateRange(dateStrings as [string, string]);
              }}
              style={{ width: '95%' }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prism Status
            </label>
            <Select
              mode="multiple"
              placeholder="Select prism statuses"
              value={prismStatus}
              onChange={(values) => setPrismStatus(values)}
              allowClear
              style={{ width: '100%' }}
              options={Object.keys(WIRE_PAYMENT_PRISM_STATUSES_TEXT).map(
                (key) => ({
                  label:
                    WIRE_PAYMENT_PRISM_STATUSES_TEXT[
                      key as keyof typeof WIRE_PAYMENT_PRISM_STATUSES_TEXT
                    ],
                  value: key,
                })
              )}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contract Care
            </label>
            <Select
              placeholder="Select"
              value={contractCare !== null ? String(contractCare) : undefined}
              onChange={(value) => setContractCare(value === 'true')}
              allowClear
              style={{ width: '100%' }}
              options={[
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' },
              ]}
            />
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default WirePaymentsFilter;
